import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActivity, useGlobalStatistics } from '../../hooks/useStatistics';
import { GroupBar } from '../../components/brand/GroupBar';
import { useCourses, useCreateCourse, useJoinCourse } from '../../hooks/useCourses';
import { useToast } from '../../components/ui/Toast';
import { Button } from '../../components/ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Karti } from '../../components/brand/Karti';
import { KartiCoachBubble, KartiState } from '../../components/brand/KartiCoach';
import { activityContext } from '../../lib/karti';
import { CourseGlyph } from '../../components/brand/GroupBar';

export default function CoursesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { data: courses, isLoading } = useCourses();
  const { data: activity } = useActivity();
  const { data: globalStats } = useGlobalStatistics();
  const createCourseMutation = useCreateCourse();
  const joinCourseMutation = useJoinCourse();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [courseName, setCourseName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseName.trim()) {
      showToast('Bitte geben Sie einen Kursnamen ein', 'error');
      return;
    }

    try {
      await createCourseMutation.mutateAsync({
        name: courseName,
        isPrivate,
      });
      showToast('Kurs erfolgreich erstellt', 'success');
      setShowCreateDialog(false);
      setCourseName('');
      setIsPrivate(false);
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Erstellen', 'error');
    }
  };

  const handleJoinCourse = async (courseId: number) => {
    try {
      await joinCourseMutation.mutateAsync(courseId);
      showToast('Erfolgreich dem Kurs beigetreten', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Beitreten', 'error');
    }
  };

  const roleLabel: Record<string, string> = { owner: 'OWNER', editor: 'EDITOR', member: 'MITGLIED' };

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <KartiState screen="loading" size={72} />
        <div className="skeleton h-10 w-2/3" />
        <div className="skeleton h-20 w-full !rounded-list" />
        <div className="skeleton h-20 w-full !rounded-list" />
        <div className="skeleton h-20 w-full !rounded-list" />
      </div>
    );
  }

  const myCourses = courses?.filter((c) => c.role === 'owner' || c.role === 'editor' || c.role === 'member') || [];
  const publicCourses = courses?.filter((c) => !c.isPrivate && !c.role) || [];

  return (
    <div className="space-y-5 animate-fadein">
      <h1 className="text-[34px] font-extrabold leading-none tracking-[-0.035em] sm:text-[38px]">
        Meine <span className="highlight">Kurse</span>
      </h1>

      <KartiCoachBubble screen="courses" ctx={activityContext(activity)} />

      {/* Section row */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="label-mono !text-xs">MEINE KURSE · {myCourses.length}</h2>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="press h-9 rounded-tile border-2 border-ink bg-cream px-3.5 text-sm font-bold shadow-hard-sm"
        >
          + Neu
        </button>
      </div>

      {myCourses.length === 0 ? (
        <div className="rounded-panel border-2.5 border-ink bg-cream p-8 text-center shadow-hard">
          <Karti className="mx-auto mb-3" size={96} mood="sleep" />
          <h3 className="text-[22px] font-bold">Noch keine Kurse</h3>
          <p className="mb-5 mt-1 text-body">Erstelle deinen ersten Kurs oder trete einem öffentlichen Kurs bei</p>
          <Button variant="accent" onClick={() => setShowCreateDialog(true)}>
            Ersten Kurs erstellen
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {myCourses.map((course, i) => (
            <div
              key={course.id}
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/app/courses/${course.id}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/app/courses/${course.id}`)}
              className="press cursor-pointer rounded-list border-2.5 border-ink bg-cream p-3.5 shadow-hard"
            >
              <div className="flex items-center gap-3">
                <CourseGlyph name={course.name} active={i === 0} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[17px] font-bold">{course.name}</div>
                  <div className="text-[13px] text-muted-foreground">
                    {course.vocabularyCount} Vokabeln · {course.memberCount} Mitglieder
                  </div>
                </div>
              </div>
              {(() => {
                const g = globalStats?.find((x) => x.courseId === course.id);
                const due = activity?.due.courses.find((x) => x.courseId === course.id)?.due;
                return (
                  <>
                    {g && <GroupBar className="mt-3" neu={g.new} inProgress={g.inProgress} learned={g.learnedVocabularies} />}
                    {due !== undefined && (
                      <div className="mt-2 font-mono text-[11px] font-bold uppercase tracking-[0.06em]">
                        {due > 0 ? `${due} fällig` : 'Alles erledigt'}
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="mt-3 flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.06em]">
                <span className="rounded-chip border-[1.5px] border-ink px-[7px] py-1">
                  {roleLabel[course.role] ?? course.role}
                </span>
                <span className="text-muted-foreground">{course.isPrivate ? 'Privat' : 'Öffentlich'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Public Courses */}
      {publicCourses.length > 0 && (
        <>
          <h2 className="label-mono !text-xs">ÖFFENTLICHE KURSE · {publicCourses.length}</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {publicCourses.map((course) => (
              <div
                key={course.id}
                className="flex flex-col gap-3 rounded-list border-2.5 border-ink bg-cream p-3.5 shadow-hard"
              >
                <div className="flex items-center gap-3">
                  <CourseGlyph name={course.name} />
                  <div className="min-w-0 flex-1">
                    <div className="line-clamp-2 text-[17px] font-bold leading-tight">{course.name}</div>
                    <div className="text-[13px] text-muted-foreground">
                      {course.vocabularyCount} Vokabeln · {course.memberCount} Mitglieder
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => handleJoinCourse(course.id)}
                  disabled={joinCourseMutation.isPending}
                >
                  Beitreten
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create Course Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <form onSubmit={handleCreateCourse}>
            <DialogHeader>
              <DialogTitle>Neuen Kurs erstellen</DialogTitle>
              <DialogDescription>
                Erstellen Sie einen neuen Vokabelkurs
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="courseName" className="label-mono">
                  Kursname
                </label>
                <Input
                  id="courseName"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="z.B. Englisch Grundwortschatz"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="visibility" className="label-mono">
                  Sichtbarkeit
                </label>
                <Select
                  id="visibility"
                  value={isPrivate ? 'private' : 'public'}
                  onChange={(e) => setIsPrivate(e.target.value === 'private')}
                >
                  <option value="public">Öffentlich</option>
                  <option value="private">Privat</option>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                Abbrechen
              </Button>
              <Button type="submit" variant="accent" disabled={createCourseMutation.isPending}>
                {createCourseMutation.isPending ? 'Erstelle...' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
